import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, CheckCircle, Clock, Package, MessageSquare, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { connect, disconnect } from '../utils/websocket';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const notificationRef = useRef(null);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      let response;
      
      // Check if user has notification history limit
      const limit = user?.notification_history_limit;
      if (limit === 0) {
        // Notifications are turned off
        setNotifications([]);
        setUnreadCount(0);
        return;
      } else if (limit) {
        // User has a specific limit
        response = await api.get(`/notifications?limit=${limit}`);
      } else {
        // No limit set, fetch all notifications
        response = await api.get('/notifications');
      }
      
      setNotifications(response.data);
      
      // Only fetch unread count if notifications are enabled
      if (limit !== 0) {
        const unreadResponse = await api.get('/notifications/unread-count');
        setUnreadCount(unreadResponse.data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/mark-read`);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    
    // Navigate to the product and scroll to the note
    if (notification.data?.product_id) {
      // Open the product and show the note
      const productEvent = new CustomEvent('openProductForNote', {
        detail: {
          productId: notification.data.product_id,
          noteId: notification.data.note_id
        }
      });
      window.dispatchEvent(productEvent);
    }
    
    setIsOpen(false);
  };

  useEffect(() => {
    fetchNotifications();
    
    // Set up WebSocket for real-time notifications
    if (user && user.id) {
      const handleNotification = (data) => {
        const notification = JSON.parse(data);
        if (notification.type === 'notification') {
          // Check if notifications are enabled
          const limit = user.notification_history_limit;
          if (limit === 0) {
            // Notifications are turned off, don't show or store
            return;
          }
          
          // Apply limit if set
          setNotifications(prev => {
            const newNotifications = [notification.notification, ...prev];
            if (limit && newNotifications.length > limit) {
              return newNotifications.slice(0, limit);
            }
            return newNotifications;
          });
          
          setUnreadCount(prev => prev + 1);
          
          // Show toast notification
          toast({
            title: notification.notification.title,
            description: notification.notification.message,
          });
        }
      };
      
      connect(user.id, handleNotification);
    }
    
    // Set up polling every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    
    return () => {
      clearInterval(interval);
      disconnect();
    };
  }, [user, toast]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Refetch notifications when user settings change
  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user?.notification_history_limit]);

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'tagged_in_note':
        return <MessageSquare className="w-4 h-4 text-blue-500" />;
      case 'product_assigned':
        return <Package className="w-4 h-4 text-green-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="relative" ref={notificationRef}>
      {/* Notification Bell */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => user?.notification_history_limit !== 0 && setIsOpen(!isOpen)}
        className={`relative p-2 ${user?.notification_history_limit === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
        disabled={user?.notification_history_limit === 0}
        title={user?.notification_history_limit === 0 ? 'Notifications are disabled' : 'Notifications'}
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500 text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </Button>

      {/* Notification Dropdown */}
      {isOpen && (
        <Card className="absolute right-0 mt-2 w-80 z-50 shadow-lg border">
          <CardContent className="p-0">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-sm">Notifications</h3>
              <div className="flex items-center space-x-2">
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={markAllAsRead}
                    className="text-xs"
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Mark all read
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                  className="p-1 h-6 w-6"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-8">
                  <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No notifications</p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-3 border-b hover:bg-gray-50 cursor-pointer transition-colors ${
                      !notification.is_read ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <p className={`text-sm font-medium ${
                            !notification.is_read ? 'text-gray-900' : 'text-gray-700'
                          }`}>
                            {notification.title}
                          </p>
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            {!notification.is_read && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            )}
                            <span className="text-xs text-gray-500">
                              {formatTimeAgo(notification.created_at)}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {notification.message}
                        </p>
                        {notification.data?.product_title && (
                          <div className="flex items-center space-x-1 mt-2">
                            <Package className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-500">
                              {notification.data.product_title}
                            </span>
                            <ExternalLink className="w-3 h-3 text-gray-400" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default NotificationBell;